import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "./cn";
import { sxToStyle } from "./sx";

export const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg font-medium whitespace-nowrap transition-[background-color,border-color,box-shadow,filter] select-none outline-none focus-visible:ring-3 focus-visible:ring-accent/25 disabled:pointer-events-none disabled:opacity-55 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:!text-current",
  {
    variants: {
      variant: {
        primary: "bg-accent text-white shadow-card hover:brightness-110",
        secondary: "border border-line bg-panel text-text shadow-card hover:border-line-strong hover:bg-[#f9f9fa]",
        ghost: "text-muted hover:bg-black/[0.045] hover:text-fg",
        danger: "bg-danger text-white shadow-card hover:brightness-110",
        success: "bg-success text-white shadow-card hover:brightness-110",
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-2.5 text-[12.5px]",
        md: "h-9 px-3.5 text-[13.5px]",
        lg: "h-10 px-4 text-[14px]",
        icon: "size-9",
        "icon-sm": "size-8",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

/** Qayd button. `loading` shows a spinner and disables the button. */
export function Button({ className, variant, size, loading, disabled, children, type = "button", ...props }) {
  return (
    <button type={type} className={cn(buttonVariants({ variant, size }), className)} disabled={disabled || loading} {...props}>
      {loading && <Loader2 className="animate-spin" />}
      {children}
    </button>
  );
}

// Background colours existing screens pass, mapped to the variant they mean.
const TONE_BY_COLOR = {
  "#5b5bd6": "primary", // Colors.primary / bluishCyan
  "#4646c8": "primary", // Colors.green
  "linear-gradient(180deg, #6464dc 0%, #5151cf 100%)": "primary", // Colors.tableGradient
  "#dc2626": "danger", // Colors.danger
  "#e7e7ea": "secondary", // Colors.iron
  "#8f929b": "secondary", // Colors.greyShade
  "#62646c": "secondary", // Colors.cloudyGrey
  "#b8bac2": "secondary", // Colors.greyCloud
  transparent: "secondary",
};
const toneOf = (c) => (typeof c === "string" ? TONE_BY_COLOR[c.trim().toLowerCase()] : undefined);

/**
 * Drop-in replacement for components/Buttons `PrimaryButton`
 * (props: title, loading, icon, src, startIcon, endIcon, type, onClick, disabled, fullWidth, bgcolor,
 * textColor, color="secondary", style, buttonStyle/sx (MUI sx, layout keys only)).
 * Background colours from bgcolor/style/buttonStyle become the matching variant (primary, danger,
 * secondary); any other colour is applied as-is.
 */
export function PrimaryButton({
  title,
  icon,
  src,
  startIcon,
  endIcon,
  fullWidth,
  bgcolor,
  textColor,
  className,
  children,
  buttonStyle,
  sx,
  style,
  color,
  variant,
  ...props
}) {
  const extra = { ...sxToStyle(sx), ...sxToStyle(buttonStyle), ...style };
  const bg = bgcolor ?? extra.background ?? extra.backgroundColor;
  delete extra.background;
  delete extra.backgroundColor;
  const tone = variant && variant !== "contained" ? variant : color === "secondary" ? "secondary" : bg ? toneOf(bg) : "primary";
  if (bg && !tone) extra.background = bg;
  if (textColor) extra.color = textColor;
  const lead = src ? <img src={src} alt="" className="size-[18px]" /> : (icon ?? startIcon);
  return (
    <Button variant={tone || "primary"} className={cn("min-w-24", fullWidth && "w-full", className)} style={extra} {...props}>
      {lead}
      {title ?? children}
      {endIcon}
    </Button>
  );
}
